import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Login.css";
import "../../styles/FormInputs.css";
import blockClubLogo from "../../media/blockclub-logo.png";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";

function Signup() {
  const { handleLoginSuccess } = useAuth();
  // Multi-step state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;

  // Get selected service from URL params (default to loyalty_text)
  const [searchParams] = useSearchParams();
  const selectedService = searchParams.get("service") || "loyalty_text";

  // Form data
  const [username, setUsername] = useState("");
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [personalPhoneNumber, setPersonalPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isEmailAvailable, setIsEmailAvailable] = useState(null);
  const [companyCheck, setCompanyCheck] = useState({ ok: null, message: "" });

  // Trial availability state
  const [trialAvailable, setTrialAvailable] = useState(null);
  const [trialCheckMessage, setTrialCheckMessage] = useState("");
  const [isCheckingTrial, setIsCheckingTrial] = useState(false);

  // ZIP helpers
  const toZipDigits = (v) => v.replace(/\D/g, "").slice(0, 9); // keep max 9 digits
  const formatZip = (digits) =>
    digits.length <= 5 ? digits : `${digits.slice(0, 5)}-${digits.slice(5)}`;

  const isZipValid = (zip) => {
    const d = zip.replace(/\D/g, "");
    return d.length === 5 || d.length === 9;
  };
  const [zipError, setZipError] = useState("");

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const isPasswordStrong = (pwd) =>
    /[A-Z]/.test(pwd) &&
    /[a-z]/.test(pwd) &&
    /[^A-Za-z0-9]/.test(pwd) &&
    pwd.length >= 6;

  const isPhoneValid = (phone) => {
    if (!phone) return false; // Phone is required
    // Basic US phone number validation (10 digits with optional formatting)
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10;
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setPersonalPhoneNumber(formattedPhoneNumber);
  };

  const handleZipChange = (e) => {
    const raw = e.target.value;

    // If the user typed any letters, show a gentle error
    if (/[A-Za-z]/.test(raw)) {
      setZipError("Please enter numbers only.");
    } else {
      setZipError("");
    }

    const digits = toZipDigits(raw);      // strip non-digits, cap at 9
    setZipCode(formatZip(digits));        // format -> 12345 or 12345-6789
  };

  const handleZipBlur = () => {
    const digits = zipCode.replace(/\D/g, "");
    if (zipCode && !(digits.length === 5 || digits.length === 9)) {
      setZipError("Enter a valid ZIP (12345 or 12345-6789).");
    } else {
      setZipError("");
    }
  };

  // Step validation functions
  const validateStep1 = () =>
    companyName.trim() !== "" &&
    isZipValid(zipCode) &&
    isPhoneValid(personalPhoneNumber);

  const validateStep2 = () => {
    return (
      username.trim() !== "" &&
      email.trim() !== "" &&
      password.trim() !== "" &&
      confirmPassword.trim() !== "" &&
      isEmailValid === true &&
      isEmailAvailable === true &&    
      password === confirmPassword &&
      isPasswordStrong(password) &&
      isUsernameAvailable === true
    );
  };


  // Check trial availability for the company
  const checkTrialAvailability = async (companyId, serviceType) => {
    setIsCheckingTrial(true);
    try {
      // Map frontend service types to backend trial types
      let trialType;
      if (serviceType === "invoice_scanning") {
        trialType = "invoice_scanning";
      } else if (serviceType === "loyalty_text") {
        trialType = "loyalty_sms";
      } else if (serviceType === "both") {
        // For "both", we need to check both trial types
        return await checkBothTrials(companyId);
      } else {
        trialType = "invoice_scanning"; // Default
      }

      const response = await fetch(`${apiUrl}/trials/check-availability?company_id=${companyId}&trial_type=${trialType}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to check trial availability");
      }

      const data = await response.json();
      setTrialAvailable(data.is_available);
      setTrialCheckMessage(data.message);
      return data.is_available;
    } catch (error) {
      console.error("Trial availability check error:", error);
      setTrialCheckMessage("Unable to verify trial availability. Please try again.");
      setTrialAvailable(false);
      return false;
    } finally {
      setIsCheckingTrial(false);
    }
  };

  // Check availability for both services
  const checkBothTrials = async (companyId) => {
    try {
      const [invoiceResponse, loyaltyResponse] = await Promise.all([
        fetch(`${apiUrl}/trials/check-availability?company_id=${companyId}&trial_type=invoice_scanning`),
        fetch(`${apiUrl}/trials/check-availability?company_id=${companyId}&trial_type=loyalty_sms`)
      ]);

      if (!invoiceResponse.ok || !loyaltyResponse.ok) {
        throw new Error("Failed to check trial availability");
      }

      const invoiceData = await invoiceResponse.json();
      const loyaltyData = await loyaltyResponse.json();

      const bothAvailable = invoiceData.is_available && loyaltyData.is_available;
      
      if (bothAvailable) {
        setTrialCheckMessage("Both trials are available for your company");
      } else if (!invoiceData.is_available && !loyaltyData.is_available) {
        setTrialCheckMessage("Your company has already used both free trials");
      } else if (!invoiceData.is_available) {
        setTrialCheckMessage("Your company has already used the Invoice Scanning free trial");
      } else {
        setTrialCheckMessage("Your company has already used the Loyalty SMS free trial");
      }

      setTrialAvailable(bothAvailable);
      return bothAvailable;
    } catch (error) {
      console.error("Both trials availability check error:", error);
      setTrialCheckMessage("Unable to verify trial availability. Please try again.");
      setTrialAvailable(false);
      return false;
    }
  };

  const runStep1Check = async () => {
    setIsCheckingTrial(true);
    try {
      const resp = await fetch(`${apiUrl}/auth/check-company-and-phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: companyName,
          zip_code: zipCode,
          personal_phone_number: personalPhoneNumber,
        }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setCompanyCheck({ ok: false, message: data.detail || "Could not verify company/phone." });
        return false;
      }

      if (data.ok === false) {
        setCompanyCheck({ ok: false, message: data.message || "Already in use." });
        return false;
      }

      setCompanyCheck({ ok: true, message: "" });
      return true;
    } catch (e) {
      setCompanyCheck({ ok: false, message: e.message || "Unable to verify company/phone." });
      return false;
    } finally {
      setIsCheckingTrial(false);
    }
  };

  // Navigation functions
  const handleNext = async () => {
    setSubmitted(true);

    if (currentStep === 1 && validateStep1()) {
      const ok = await runStep1Check();
      if (ok) {
        setCurrentStep(2);
        setSubmitted(false);
      }
      return;
    }

    if (currentStep === 2 && validateStep2()) {
      handleSignup();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setSubmitted(false);
      // Clear trial availability status when going back
      setTrialAvailable(null);
      setTrialCheckMessage("");
    }
  };

  // Debounced username availability check
  useEffect(() => {
    const delay = setTimeout(() => {
      if (username.length < 3) {
        setIsUsernameAvailable(null);
        return;
      }

      fetch(`${apiUrl}/auth/check-username`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      })
        .then((res) => res.json())
        .then((data) => setIsUsernameAvailable(data.available))
        .catch(() => setIsUsernameAvailable(null));
    }, 500);

    return () => clearTimeout(delay);
  }, [username, apiUrl]);

  // Debounced email format validation
  useEffect(() => {
    const delay = setTimeout(() => {
      if (email) {
        setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      } else {
        setIsEmailValid(null);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [email]);

  useEffect(() => {
    const delay = setTimeout(async () => {
      // only check availability if format is valid
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
      if (!email || !valid) {
        setIsEmailAvailable(null);
        return;
      }
      try {
        const res = await fetch(`${apiUrl}/auth/check-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const data = await res.json();
        setIsEmailAvailable(data.available);
      } catch {
        setIsEmailAvailable(null);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [email, apiUrl]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      // only check when all three are present & phone format is valid
      if (!companyName || !zipCode || !isPhoneValid(personalPhoneNumber)) {
        setCompanyCheck({ ok: null, message: "" });
        return;
      }

      try {
        const resp = await fetch(`${apiUrl}/auth/check-company-and-phone`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_name: companyName,
            zip_code: zipCode,
            personal_phone_number: personalPhoneNumber,
          }),
        });
        const data = await resp.json();

        if (!resp.ok) {
          setCompanyCheck({
            ok: false,
            message: data.detail || "Could not verify company/phone.",
          });
          return;
        }

        if (data.ok === false) {
          // server returns a clear message for both company_taken and phone_taken
          setCompanyCheck({ ok: false, message: data.message || "Already in use." });
        } else {
          setCompanyCheck({ ok: true, message: "" });
        }
      } catch {
        // network errors: don't block typing
        setCompanyCheck({ ok: null, message: "" });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companyName, zipCode, personalPhoneNumber, apiUrl]);

  const handleSignup = async () => {
    setSubmitted(true);

    if (isEmailValid && isEmailAvailable === false) {
      alert("This email is already registered. Please sign in.");
      return;
    }

    if (
      !username ||
      !email ||
      !companyName ||
      !zipCode ||
      !password ||
      !confirmPassword ||
      !isEmailValid ||
      password !== confirmPassword ||
      !isPasswordStrong(password) ||
      isUsernameAvailable === false ||
      !isPhoneValid(personalPhoneNumber)
    )
      return;

    try {
      // Show success message based on the service type
      const serviceDisplayName = selectedService === "invoice_scanning" 
        ? "Invoice Scanning" 
        : selectedService === "loyalty_text" 
        ? "Loyalty Text Program"
        : "Both Services";

      // First, get or create the company
      let companyId;
      
      try {
        // Try to lookup existing company
        const companyLookupResponse = await fetch(`${apiUrl}/company/lookup-id?company_name=${encodeURIComponent(companyName)}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (companyLookupResponse.ok) {
          const companyData = await companyLookupResponse.json();
          companyId = companyData.company_id;
        } else {
          // Company doesn't exist, we'll need to create it
          // trial signup creates company
          throw new Error("Company needs to be created during trial signup");
        }
      } catch (companyError) {
        // If company lookup fails, we'll let trial signup handle company creation
        // This assumes your trial endpoints will create companies if they don't exist
      }

      // Determine which trial endpoint to use based on selected service
      let trialEndpoint;
      if (selectedService === "invoice_scanning") {
        trialEndpoint = "/trials/signup/invoice";
      } else if (selectedService === "loyalty_text") {
        trialEndpoint = "/trials/signup/loyalty";
      } else if (selectedService === "both") {
        trialEndpoint = "/trials/signup/both";
      } else {
        // Default to invoice scanning
        trialEndpoint = "/trials/signup/invoice";
      }

      // Register user with trial
      const requestBody = {
        username,
        email,
        password,
      };

      // Add personal phone number (required field)
      if (personalPhoneNumber && isPhoneValid(personalPhoneNumber)) {
        requestBody.personal_phone_number = personalPhoneNumber;
      }

      // Add company_id if we have it, otherwise add company info for creation
      if (companyId) {
        requestBody.company_id = companyId;
      } else {
        // Fallback: include company info for backend to create company
        // Note: This requires updating your TrialSignupRequest model
        requestBody.company_name = companyName;
        requestBody.zip_code = zipCode;
      }

      const trialResponse = await fetch(`${apiUrl}${trialEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!trialResponse.ok) {
        const error = await trialResponse.json();
        
        // Handle specific trial conflict errors with better messages
        if (trialResponse.status === 409) {
          throw new Error(`A user from your company has already used the ${serviceDisplayName} free trial.`);
        } else if (trialResponse.status === 400 && error.detail && error.detail.includes("already used")) {
          throw new Error(`Your company has already used the ${serviceDisplayName} free trial.`);
        } else {
          throw new Error(error.detail || "Signup failed. Please try again.");
        }
      }

      await trialResponse.json();

      // Auto-login the user after successful signup
      handleLoginSuccess(email);

      alert(`Account created successfully for ${serviceDisplayName}! You are now logged in.`);
      navigate("/");
    } catch (error) {
      alert(error.message || "Signup failed. Please try again.");
    }
  };

  const renderProgressBar = () => (
    <div className="progress-bar-container">
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }} /* Dynamic width calculation - must remain inline */
        ></div>
      </div>
      <div className="progress-steps">
        <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
          <span>1</span>
          <label>Company Info</label>
        </div>
        <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
          <span>2</span>
          <label>Account Setup</label>
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <>
      <h3 className="step-title">Company Information</h3>
      
      {/* Company Name */}
      <div className="form-group">
        <label className="form-label">Company Name:</label>
        <p className="field-instruction">Enter your business or organization name</p>
        <input
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="e.g., Acme Restaurant"
          className="standard-input"
        />
        {submitted && !companyName && (
          <p className="mismatch-warning">Company name is required</p>
        )}
        {companyCheck.ok === false && (
          <p className="mismatch-warning">{companyCheck.message}</p>
        )}
      </div>

      {/* Zip Code */}
      <div className="form-group">
        <label className="form-label">Zip Code:</label>
        <p className="field-instruction">Enter your company's location zip code</p>
        <input
          value={zipCode}
          onChange={handleZipChange}
          onBlur={handleZipBlur}             // optional
          placeholder="12345 or 12345-6789"
          maxLength={10}
          inputMode="numeric"
          pattern="\d*"
          autoComplete="postal-code"
          className="standard-input"
        />

        {zipError && (
          <p className="mismatch-warning">{zipError}</p>
        )}

        {submitted && !isZipValid(zipCode) && (
          <p className="mismatch-warning">Enter a valid ZIP (12345 or 12345-6789)</p>
        )}
      </div>

      {/* Personal Phone Number */}
      <div className="form-group">
        <label className="form-label">Personal Phone Number:</label>
        <p className="field-instruction">Enter your personal phone number for account recovery and notifications</p>
        <input
          type="tel"
          value={personalPhoneNumber}
          onChange={handlePhoneChange}
          placeholder="(555) 123-4567"
          maxLength="14"
          className="standard-input"
        />
        {submitted && !personalPhoneNumber && (
          <p className="mismatch-warning">Personal phone number is required</p>
        )}
        {personalPhoneNumber && !isPhoneValid(personalPhoneNumber) && (
          <p className="mismatch-warning">Please enter a valid 10-digit phone number</p>
        )}
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <h3 className="step-title">Account Setup</h3>
      
      {/* Username */}
      <div className="form-group">
        <label className="form-label">Username:</label>
        <p className="field-instruction">Choose a unique username (minimum 3 characters)</p>
        <div className="validation-icon-wrapper">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Choose a username"
            className="standard-input"
          />
          {username && isUsernameAvailable !== null && (
            <span className="validation-icon">
              {isUsernameAvailable ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </span>
          )}
        </div>
        {submitted && !username && (
          <p className="mismatch-warning">Username is required</p>
        )}
        {username.length > 0 && username.length < 3 && (
          <p className="mismatch-warning">Username must be at least 3 characters</p>
        )}
        {username && isUsernameAvailable === false && (
          <p className="mismatch-warning">Username is already taken</p>
        )}
      </div>

      {/* Email */}
      <div className="form-group">
        <label className="form-label">Email:</label>
        <p className="field-instruction">Enter a valid email address for your account</p>
        <div className="validation-icon-wrapper">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="standard-input"
          />
          {email && isEmailValid !== null && (
            <span className="validation-icon">
              {isEmailValid ? (
                <FaCheckCircle color="green" />
              ) : (
                <FaTimesCircle color="red" />
              )}
            </span>
          )}
        </div>
        {submitted && !email && (
          <p className="mismatch-warning">Email is required</p>
        )}
        {email && !isEmailValid && (
          <p className="mismatch-warning">Please enter a valid email</p>
        )}
        {email && isEmailValid && isEmailAvailable === false && (
          <p className="mismatch-warning">This email is already registered. Please sign in.</p>
        )}
      </div>

      {/* Password */}
      <div className="form-group">
        <label className="form-label">Password:</label>
        <p className="field-instruction">Create a secure password (6+ characters, uppercase, special character)</p>
        <div className="standard-password-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="standard-input"
          />
          {password && (
            <span
              className="standard-password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </span>
          )}
        </div>
        {submitted && !password && (
          <p className="mismatch-warning">Password is required</p>
        )}
        {password && !isPasswordStrong(password) && (
          <p className="mismatch-warning">
            Password must include uppercase, lowercase, special character, and be 6+ characters
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="form-group">
        <label className="form-label">Confirm Password:</label>
        <p className="field-instruction">Re-enter your password to confirm</p>
        <div className="standard-password-wrapper">
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            className="standard-input"
          />
          {confirmPassword && (
            <span
              className="standard-password-toggle"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
            >
              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
            </span>
          )}
        </div>
        {submitted && !confirmPassword && (
          <p className="mismatch-warning">Please confirm your password</p>
        )}
        {confirmPassword && password !== confirmPassword && (
          <p className="mismatch-warning">Passwords do not match</p>
        )}
      </div>
    </>
  );

  return (
    <div className="signup-wrapper">
      <div className="login-container">
        <div className="login-box multi-step">
          <img src={blockClubLogo} alt="Block Club logo" className="block-club-logo" />
          <h2 className="login-title">
            Create Account - {selectedService === "invoice_scanning" 
              ? "Invoice Scanning" 
              : selectedService === "loyalty_text" 
              ? "Loyalty Text Program"
              : "Both Services"}
          </h2>
          
          {renderProgressBar()}
          
          <div className="step-content">
            {currentStep === 1 ? renderStep1() : renderStep2()}
          </div>

          <div className="step-navigation">
            {currentStep > 1 ? (
              <button onClick={handleBack} className="back-button">
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="back-button"
              >
                Back to Login
              </button>
            )}
            <button 
              onClick={handleNext} 
              className="login-button next-button"
              disabled={
                (currentStep === 1 && (
                  !companyName ||
                  !isZipValid(zipCode) ||
                  !isPhoneValid(personalPhoneNumber) ||
                  companyCheck.ok === false ||
                  isCheckingTrial
                )) ||
                (currentStep === 2 && !validateStep2())
              }
            >
              {isCheckingTrial && currentStep === 1 
                ? "Checking..." 
                : currentStep === totalSteps ? "Create Account" : "Next"}
            </button>
          </div>

          <div className="bottom-text">
            Already have an account?{" "}
            <span className="link" onClick={() => navigate("/login")}>
              Login
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
